import React, { useEffect, useRef, useState } from "react";
import { Grid, Card, CardContent, Divider, Switch, FormControlLabel } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";
import MDButton from "../../components/MDButton";
import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";
import Loader from "../Loader/Loader";
import PreviewWindow from "../../components/PreviewWindow/PreviewWindow";
import { Player } from "video-react";
import { getStoreCampaigns } from "../../assets/firebaseFirestore";
import Placements from "../../json/placements.json";
import "video-react/styles/scss/video-react.scss";

const MEDIA_CONFIG = {
  image: {
    mobileLabel: "Mobile Banner",
    desktopLabel: "Desktop Banner",
    mobileRecommendation: "Recommended size: 400 × 250 pixels",
    desktopRecommendation: "Recommended size: 800 × 200 pixels",
    accept: "image/*",
    helperText: "Accepted formats: JPG, PNG, GIF, WebP",
    icon: "image",
  },
  video: {
    mobileLabel: "Video File",
    desktopLabel: "Desktop Video",
    mobileRecommendation: "Recommended: MP4, max 30 seconds, 1080x1920 (16:9)",
    desktopRecommendation: "Recommended: MP4, max 30 seconds, 1920x1080 (16:9)",
    accept: "video/*",
    helperText: "Accepted formats: MP4, WebM, MOV",
    icon: "videocam",
  },
};

const CreateAdForm = ({
                        onSubmit,
                        changeValue,
                        image,
                        defaultAd,
                        setImage,
                        values,
                        image2,
                        setImage2,
                        video,
                        setVideo,
                      }) => {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [previewWindow, setPreviewWindow] = useState(false);

  const mediaType = values?.mediaType ?? "image";
  const mediaConfig = MEDIA_CONFIG[mediaType];
  const formRef = useRef();

  useEffect(() => {
    const loadData = async () => {
      try {
        const campaignsData = await getStoreCampaigns();
        const activeCampaigns = campaignsData
          .filter((c) => c.status === "active" || c.status === "draft")
          .map((c) => ({ label: c.name, id: c.id, ...c }));

        setCampaigns(activeCampaigns);

        if (defaultAd?.campaignId) {
          setSelectedCampaign(activeCampaigns.find((c) => c.id === defaultAd.campaignId) ?? null);
        }

        setTimeout(() => {
          if (Placements?.length > 0) setDataLoaded(true);
        }, 500);
      } catch (error) {
        console.error("Error loading campaigns:", error);
        setDataLoaded(true);
      } finally {
        setLoadingCampaigns(false);
      }
    };

    loadData();
  }, [defaultAd]);

  const handleCampaignChange = (_, newValue) => {
    setSelectedCampaign(newValue);
    changeValue(newValue?.id ?? "", "campaignId");
  };

  const handleMediaTypeChange = (e) => {
    const newType = e.target.checked ? "video" : "image";
    changeValue(newType, "mediaType");
    setImage(null);
    setImage2(null);
  };

  const renderMediaPreview = (mediaFile, defaultMedia, isMobile = true) => {
    let previewUrl;

    if (mediaFile) {
      previewUrl = URL.createObjectURL(mediaFile);
    } else if (mediaType === "video") {
      previewUrl = defaultMedia?.link ?? values?.video?.link;
    } else {
      previewUrl = defaultMedia?.link;
    }

    const maxHeight = isMobile ? "200px" : "150px";
    const label = isMobile ? mediaConfig.mobileLabel : mediaConfig.desktopLabel;

    // ✅ Guard: if nothing resolved, show upload prompt
    if (!previewUrl) {
      return (
        <MDTypography variant="caption" color="text">
          Click below to upload {label}
        </MDTypography>
      );
    }

    if (mediaType === "video") {
      return <Player playsInline src={previewUrl} />;
    }

    return (
      <img
        src={previewUrl}
        alt={label}
        style={{ maxWidth: "100%", maxHeight, objectFit: "contain" }}
      />
    );
  };

  const usedPlacementIds = selectedCampaign?.campaignAds?.flatMap((el) =>
    Array.isArray(el.placement) ? el.placement.map((p) => p.id) : [el.placement.id],
  ) ?? [];

  if (!dataLoaded || loadingCampaigns) return <Loader />;

  return (
    <form ref={formRef} onSubmit={onSubmit}>
      <Grid container padding={3} spacing={3}>

        {/* Media Section */}
        <Grid item xs={12} lg={5}>
          <Card>
            <CardContent>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <MDTypography variant="h5" fontWeight="medium">
                  Media Assets
                </MDTypography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={mediaType === "video"}
                      onChange={handleMediaTypeChange}
                      color="primary"
                    />
                  }
                  label={
                    <MDBox display="flex" alignItems="center">
                      <Icon sx={{ mr: 0.5, fontSize: "1.2rem" }}>{mediaConfig.icon}</Icon>
                      <MDTypography variant="button" fontWeight="medium">
                        {mediaType === "video" ? "Video" : "Image"}
                      </MDTypography>
                    </MDBox>
                  }
                  labelPlacement="start"
                  sx={{ margin: 0 }}
                />
              </MDBox>

              {/* Mobile / Video upload */}
              <MDBox mb={3}>
                <MDTypography variant="button" fontWeight="regular" color="text" mb={1} display="block">
                  {mediaConfig.mobileLabel}
                </MDTypography>
                <MDTypography variant="caption" color="text" mb={2} display="block">
                  {mediaConfig.mobileRecommendation}
                </MDTypography>
                <MDBox sx={previewBoxStyles("200px")}>
                  {renderMediaPreview(mediaType === "video" ? video : image, defaultAd?.image, true)}
                </MDBox>
                <TextField
                  fullWidth
                  onChange={(e) => mediaType === "video" ? setVideo(e.target.files[0]) : setImage(e.target.files[0])}
                  required={mediaType === "video" ? !values?.video : !values?.image}
                  inputProps={{ accept: mediaConfig.accept }}
                  type="file"
                  size="small"
                  helperText={mediaConfig.helperText}
                />
              </MDBox>

              {/* Desktop upload (image only) */}
              {mediaType !== "video" && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <MDBox>
                    <MDTypography variant="button" fontWeight="regular" color="text" mb={1} display="block">
                      {mediaConfig.desktopLabel}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" mb={2} display="block">
                      {mediaConfig.desktopRecommendation}
                    </MDTypography>
                    <MDBox sx={previewBoxStyles("150px")}>
                      {renderMediaPreview(image2, defaultAd?.desktop_image, false)}
                    </MDBox>
                    <TextField
                      fullWidth
                      onChange={(e) => setImage2(e.target.files[0])}
                      required={!values?.desktop_image}
                      inputProps={{ accept: mediaConfig.accept }}
                      type="file"
                      size="small"
                      helperText={mediaConfig.helperText}
                    />
                  </MDBox>
                </>
              )}

            </CardContent>
          </Card>
        </Grid>

        {/* Form Fields Section */}
        <Grid item xs={12} lg={7}>
          <Card>
            <CardContent>
              <MDTypography variant="h5" fontWeight="medium" mb={3}>
                Advertisement Details
              </MDTypography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <MDTypography variant="button" fontWeight="regular" color="text" mb={1} display="block">
                    Title *
                  </MDTypography>
                  <TextField
                    fullWidth
                    value={values?.title}
                    onChange={(e) => changeValue(e.target.value, "title")}
                    required
                    placeholder="Enter advertisement title"
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <MDTypography variant="button" fontWeight="regular" color="text" mb={1} display="block">
                    Custom note (not visible for client) *
                  </MDTypography>
                  <TextField
                    fullWidth
                    value={values?.description}
                    onChange={(e) => changeValue(e.target.value, "description")}
                    required
                    multiline
                    rows={3}
                    placeholder="Enter advertisement description"
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <MDTypography variant="button" fontWeight="regular" color="text" mb={1} display="block">
                    Link URL *
                  </MDTypography>
                  <TextField
                    fullWidth
                    value={values?.link}
                    onChange={(e) => changeValue(e.target.value, "link")}
                    required
                    type="url"
                    placeholder="https://example.com"
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <MDTypography variant="button" fontWeight="regular" color="text" mb={1} display="block">
                    Select the Status *
                  </MDTypography>
                  <Autocomplete
                    value={values?.status}
                    onChange={(e, val) => {
                      changeValue(val, "status");
                    }}
                    options={["Publish", "Unpublish"]}
                    getOptionLabel={(option) => option ?? ""}
                    noOptionsText="No statuses available."
                    renderInput={(params) => (
                      <TextField {...params} required placeholder="Select a campaign" size="small" />
                    )}
                  />
                </Grid>


                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Campaign Information
                  </MDTypography>
                </Grid>

                <Grid item xs={12}>
                  <MDTypography variant="button" fontWeight="regular" color="text" mb={1} display="block">
                    Select Campaign *
                  </MDTypography>
                  <Autocomplete
                    value={selectedCampaign}
                    onChange={handleCampaignChange}
                    options={campaigns}
                    getOptionLabel={(option) => option.label ?? ""}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText="No campaigns available. Please create a campaign first."
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <MDBox>
                          <MDTypography variant="button" fontWeight="medium">
                            {option.label}
                          </MDTypography>
                          {option.description && (
                            <MDTypography variant="caption" color="text" display="block">
                              {option.description}
                            </MDTypography>
                          )}
                          <MDTypography variant="caption" color="text" display="block">
                            Budget: ${option.budget} • Status: {option.status}
                          </MDTypography>
                        </MDBox>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} required placeholder="Select a campaign" size="small" />
                    )}
                  />
                </Grid>

                {selectedCampaign && (
                  <>
                    <Grid item xs={12}>
                      <CampaignDetails campaign={selectedCampaign} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <MDTypography variant="button" fontWeight="regular" color="text" mb={1} display="block">
                        Ad Placement *
                      </MDTypography>
                      <Autocomplete
                        fullWidth
                        multiple
                        value={values?.placement ?? []}
                        options={Placements}
                        getOptionLabel={(option) => option.label ?? ""}
                        getOptionDisabled={(option) => usedPlacementIds.includes(option.id)}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_, placement) => changeValue(placement, "placement")}
                        renderOption={(props, placement) => (
                          <li {...props} key={placement.id}>
                            {placement.label}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required={!values?.placement?.length}
                            placeholder={values?.placement?.length ? "" : "Select placement"}
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Submit */}
        <Grid item xs={12}>
          <MDBox display="flex" gap={3} justifyContent="flex-end" mt={2}>

            <MDButton type={"button"} onClick={() => {
              if (formRef.current.checkValidity()) {
                setPreviewWindow(values);
              } else {
                formRef.current.reportValidity();
              }
            }} variant="gradient" color="success" size="large">
              <Icon sx={{ fontWeight: "bold", marginRight: "8px" }}>done</Icon>
              Save Advertisement
            </MDButton>

          </MDBox>
        </Grid>
      </Grid>

      {previewWindow && <PreviewWindow setPreview={setPreviewWindow} video={video} image={image2} advertisement={values} />}
    </form>
  );
};

// Вынесен в отдельный компонент
const CampaignDetails = ({ campaign }) => (
  <Card variant="outlined" sx={{ backgroundColor: "#f5f5f5", p: 2 }}>
    <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1}>
      Selected Campaign Details
    </MDTypography>
    <Grid container spacing={1}>
      {[
        { label: "Name", value: campaign.name },
        { label: "Status", value: campaign.status },
        { label: "Budget", value: `$${campaign.budget}` },
        { label: "Duration", value: `${campaign.startDate} - ${campaign.endDate}` },
      ].map(({ label, value }) => (
        <Grid item xs={12} md={6} key={label}>
          <MDTypography variant="caption" color="text">
            <strong>{label}:</strong> {value}
          </MDTypography>
        </Grid>
      ))}
      {campaign.description && (
        <Grid item xs={12}>
          <MDTypography variant="caption" color="text">
            <strong>Description:</strong> {campaign.description}
          </MDTypography>
        </Grid>
      )}
    </Grid>
  </Card>
);

// Вынесены стили
const previewBoxStyles = (minHeight) => ({
  border: "2px dashed #d0d0d0",
  borderRadius: "8px",
  padding: "16px",
  textAlign: "center",
  backgroundColor: "#fafafa",
  minHeight,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "12px",
  position: "relative",
  overflow: "hidden",
});

export default CreateAdForm;
